import React from 'react';
import ReactDOM from 'react-dom';
import { Router } from 'react-router';
import routes from './router/routes.jsx';
import { syncHistoryWithStore } from 'react-router-redux';
import { Provider } from 'react-redux';
import configureStore from './configure_store';
import hashHistory from './history';
import {
    swEnabled,
    STORAGE_KEY,
    OLD_KEYS,
    subscribeToMessages,
    swCommand,
} from './engage_cache.js';
import rootReducer from './root_reducer.js';
import * as offlineActions from './actions/offline.js';
import { processNext } from './actions/request_queue.js';
import _ from 'underscore';
import zlib from 'zlib';

const compress = raw =>
    new Promise((resolve, reject) => {
        zlib.gzip(raw, (err, compressedBuffer) => {
            if (err) {
                return reject(err);
            }
            resolve(compressedBuffer.toString('base64'));
        });
    });

const decompress = raw =>
    new Promise((resolve, reject) => {
        zlib.gunzip(Buffer.from(raw, 'base64'), (err, uncompressed) => {
            if (err) {
                return reject(err);
            }
            resolve(uncompressed.toString());
        });
    });

async function getOfflineBackup() {
    const state = localStorage.getItem(STORAGE_KEY);

    OLD_KEYS.forEach(k => localStorage.removeItem(k));

    if (state != null) {
        try {
            let nextState = JSON.parse(state);
            nextState = rootReducer(nextState, { type: Math.random() });
            return nextState;
        } catch (e) {
            /* eslint-disable no-console */
            console.error('Stored state is not compatible');
            console.error(e);
            /* eslint-enable no-console */
            return undefined;
        }
    }
}

async function gatherInitialState() {
    if (!swEnabled) {
        return Promise.resolve(undefined);
    }

    const backup = await getOfflineBackup();
    try {
        const response = await fetch('/api/online/');
        if (502 <= response.status && response.status <= 504) {
            throw new Error('Connection Error');
        }

        if (backup) {
            const partialState = {
                requestQueue: backup.requestQueue,
            };
            return rootReducer(partialState, { type: Math.random() });
        }
        return undefined;
    } catch (e) {
        return backup;
    }
}

let canWriteState = true;
function _writeState(state) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}
const writeState = async state => {
    const {
        auralNotification,
        content,
        markup,
        notifications,
        ...remainingState
    } = state;
    if (!canWriteState) {
        return;
    }
    try {
        _writeState(state);
    } catch (e) {
        canWriteState = false;
        localStorage.removeItem(STORAGE_KEY);
        localStorage.setItem('QUOTA_FAILURE-2016-09-01', true);
    }
};

export default function boot() {
    gatherInitialState().then(initialState => {
        const store = configureStore(initialState);
        if (swEnabled) {
            subscribeToMessages(({ data }) => {
                switch (data.type) {
                    case 'REMOTE_DISPATCH': {
                        const { actionName, args } = data;
                        /* eslint-disable no-console */
                        console.log(
                            '[engage] remoteDispatch',
                            actionName,
                            args
                        );
                        /* eslint-enable no-console */
                        const action = offlineActions[actionName]; //eslint-disable-line import/namespace

                        if (typeof action !== 'function') {
                            throw new Error(
                                'Invalid action name: ',
                                actionName
                            );
                        }

                        store.dispatch(action(...args));
                        break;
                    }
                }
            });
            swCommand('echoState');
        }

        store.dispatch(processNext());

        if (process.env.NODE_ENV === 'development') {
            global.store = store;
        }

        if (swEnabled) {
            store.subscribe(
                _.debounce(() => writeState(store.getState()), 500)
            ); // Only store after a 1/2 second of inactivity
        }

        const history = syncHistoryWithStore(hashHistory, store);

        ReactDOM.render(
            <Provider store={store}>
                <Router history={history}>{routes}</Router>
            </Provider>,
            document.getElementById('react-app-placeholder')
        );
    });
}



// WEBPACK FOOTER //
// ./src/boot.jsx